import React, { useEffect, useState } from "react";
import api from "../../api";
import { useNavigate } from "react-router-dom";

const IndexNewsLetter = () => {
  const navigate = useNavigate();
  const [subscribers, setSubscribers] = useState([]);
  const [stats, setStats] = useState({ total: 0, active: 0, inactive: 0 });
  const [searchInput, setSearchInput] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const fetchSubscribers = async (page = 1, append = false, search = "") => {
    append ? setLoadingMore(true) : setLoading(true);
    setError("");
    try {
      const response = await api.get("/newsletter", {
        params: { page, search, pageSize: 12 },
      });
      const data = response.data;
      setSubscribers((prev) => append ? [...prev, ...data.subscribers] : data.subscribers);
      setStats(data.stats);
      setCurrentPage(data.pagination.page);
      setTotalPages(data.pagination.totalPages);
    } catch (err) {
      setError("Erreur lors du chargement des abonnés.");
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    fetchSubscribers(1, false, searchTerm);
  }, [searchTerm]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <p className="text-slate-100 text-xl font-semibold">Chargement des abonnés...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <main className="container mx-auto p-6">
        <section className="mb-8 overflow-hidden rounded-3xl bg-gradient-to-r from-slate-900 via-slate-800 to-violet-700 text-white shadow-sm">
          <div className="p-8 md:p-10">
            <p className="mb-3 inline-flex rounded-full bg-white/10 px-3 py-1 text-sm font-medium text-violet-100">
              Newsletter · Abonnés
            </p>
            <h1 className="mb-3 text-3xl font-bold md:text-4xl">Liste des abonnés</h1>
            <p className="max-w-2xl text-sm leading-7 text-slate-200 md:text-base">
              Consultez les personnes inscrites à la newsletter, recherchez un email précis et suivez l'état des abonnements.
            </p>
            <p className="mt-4 text-sm text-violet-100/90">
              {stats.total} abonné{stats.total > 1 ? "s" : ""} au total
            </p>
          </div>
        </section>

        <div className="mb-8 grid grid-cols-1 gap-6 md:grid-cols-3">
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <p className="text-sm font-medium text-slate-400">Total abonnés</p>
            <h2 className="mt-2 text-3xl font-bold text-white">{stats.total}</h2>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <p className="text-sm font-medium text-slate-400">Abonnés actifs</p>
            <h2 className="mt-2 text-3xl font-bold text-green-400">{stats.active}</h2>
          </div>
          <div className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <p className="text-sm font-medium text-slate-400">Désabonnés</p>
            <h2 className="mt-2 text-3xl font-bold text-red-400">{stats.inactive}</h2>
          </div>
        </div>

        <div className="grid grid-cols-1 gap-6 xl:grid-cols-4">
          <section className="xl:col-span-3 rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <div>
                <h2 className="text-xl font-semibold text-white">Rechercher un abonné</h2>
                <p className="mt-1 text-sm text-slate-400">Filtrez la liste par adresse email.</p>
              </div>
              <div className="w-full max-w-xl rounded-2xl border border-slate-800 bg-slate-950 p-4">
                <p className="mb-3 text-sm font-semibold text-white">Créer une newsletter</p>
                <p className="mb-4 text-sm text-slate-400">Choisissez le type de communication à envoyer.</p>
                <div className="flex flex-col gap-3 sm:flex-row">
                  <button
                    onClick={() => navigate("/dashboard/newsletter/create")}
                    className="flex-1 rounded-xl bg-green-600 px-5 py-3 font-semibold text-white transition hover:bg-green-500"
                  >
                    Mail libre
                  </button>
                  <button
                    onClick={() => navigate("/dashboard/newsletter/share-post")}
                    className="flex-1 rounded-xl bg-amber-500 px-5 py-3 font-semibold text-slate-950 transition hover:bg-amber-400"
                  >
                    Partager un post
                  </button>
                </div>
              </div>
            </div>

            <form
              onSubmit={(e) => { e.preventDefault(); setSearchTerm(searchInput.trim()); }}
              className="mb-6 flex flex-col gap-3 md:flex-row"
            >
              <input
                type="text"
                placeholder="Rechercher un abonné par email..."
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                className="w-full rounded-xl border border-slate-700 bg-slate-950 px-4 py-3 text-slate-100 placeholder:text-slate-500 outline-none transition focus:border-violet-500 md:w-96"
              />
              <button type="submit" className="rounded-xl bg-violet-600 px-4 py-3 font-semibold text-white transition hover:bg-violet-500">
                Rechercher
              </button>
              <button
                type="button"
                onClick={() => { setSearchInput(""); setSearchTerm(""); }}
                className="rounded-xl border border-slate-700 px-4 py-3 font-semibold text-slate-200 transition hover:bg-slate-800"
              >
                Réinitialiser
              </button>
            </form>

            {error && (
              <div className="mb-6 rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-300">{error}</div>
            )}

            <div className="overflow-x-auto rounded-2xl border border-slate-800">
              <table className="w-full border-collapse">
                <thead>
                  <tr className="bg-slate-800">
                    <th className="border-b border-slate-700 px-4 py-3 text-left text-sm font-semibold text-slate-200">Email</th>
                    <th className="border-b border-slate-700 px-4 py-3 text-left text-sm font-semibold text-slate-200">Statut</th>
                    <th className="border-b border-slate-700 px-4 py-3 text-left text-sm font-semibold text-slate-200">Date inscription</th>
                    <th className="border-b border-slate-700 px-4 py-3 text-left text-sm font-semibold text-slate-200">Date désinscription</th>
                  </tr>
                </thead>
                <tbody>
                  {subscribers.length > 0 ? (
                    subscribers.map((subscriber) => (
                      <tr key={subscriber.id} className="transition hover:bg-slate-800/60">
                        <td className="border-b border-slate-800 px-4 py-4 text-sm text-slate-200">{subscriber.email}</td>
                        <td className="border-b border-slate-800 px-4 py-4 text-sm">
                          <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold ${subscriber.isActive ? "bg-green-500/15 text-green-300" : "bg-red-500/15 text-red-300"}`}>
                            {subscriber.isActive ? "Actif" : "Désabonné"}
                          </span>
                        </td>
                        <td className="border-b border-slate-800 px-4 py-4 text-sm text-slate-300">
                          {subscriber.subscribedAt ? new Date(subscriber.subscribedAt).toLocaleDateString("fr-BE") : "-"}
                        </td>
                        <td className="border-b border-slate-800 px-4 py-4 text-sm text-slate-300">
                          {subscriber.unsubscribedAt ? new Date(subscriber.unsubscribedAt).toLocaleDateString("fr-BE") : "-"}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="4" className="px-4 py-8 text-center text-slate-400">Aucun abonné trouvé.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {currentPage < totalPages && (
              <div className="mt-8 flex justify-center">
                <button
                  onClick={() => fetchSubscribers(currentPage + 1, true, searchTerm)}
                  disabled={loadingMore}
                  className="rounded-xl bg-violet-600 px-6 py-3 font-semibold text-white transition hover:bg-violet-500 disabled:opacity-60"
                >
                  {loadingMore ? "Chargement..." : "Charger plus"}
                </button>
              </div>
            )}
          </section>

          <section className="rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <h2 className="mb-4 text-xl font-semibold text-white">Aperçu rapide</h2>
            <div className="space-y-4">
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-violet-400">Abonnés actifs</p>
                <p className="text-sm font-semibold text-white">{stats.active} destinataire{stats.active > 1 ? "s" : ""}</p>
              </div>
              <div className="rounded-2xl border border-slate-800 bg-slate-950 p-5">
                <p className="mb-2 text-xs font-medium uppercase tracking-wide text-violet-400">Recherche</p>
                <p className="text-sm leading-6 text-slate-400">
                  {searchTerm ? `Filtre actif : "${searchTerm}"` : "Aucun filtre appliqué."}
                </p>
              </div>
            </div>
          </section>
        </div>
      </main>
    </div>
  );
};

export default IndexNewsLetter;